import { useEffect, useState } from "react";

import { motion } from "framer-motion";
import { chunk } from "lodash";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { useStep } from "usehooks-ts";

import { GenericTooltip } from "@components/tooltip-container";
import { TertiaryButton } from "@shared/buttons";

import { useChartState } from "../hooks";
import { MAIN_METRIC_ID } from "../utils/chart";

import { type CustomChartDataset } from "./plugins/chart-data-manager-plugin";
import { checkShouldDrawChartLegendInChart } from "./plugins/split-chart-data-view-plugin";

type Props = {
  chartIndex: number;
  chart: ChartJSOrUndefined<any>;
};

const MAX_PER_ROW = 5;

export const ChartLegend = ({ chart, chartIndex }: Props) => {
  const { getChartAt } = useChartState();
  const chartConfig = getChartAt(chartIndex);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const datasets = chart?.data.datasets || [];

  // HACK: This is a hack to force the legend to re-render when the chart is updated
  const [HACK, setHACK] = useState(0);
  useEffect(() => {
    if (chartConfig.chartView === "split" && datasets.length < 3) {
      setHACK((prev) => prev + 1);
    }
  }, [HACK, chartConfig, datasets]);

  const chartPlugins = chart?.config.options.plugins;
  const yScaleTitles = chartPlugins?.yScaleTitles;

  const mainMetricName = yScaleTitles?.titles?.mainMetric || "";
  const compare1Name = yScaleTitles?.titles?.compare1 || "";
  const fontConfig = yScaleTitles?.font || {};
  const font = `${fontConfig.weight} ${fontConfig.size}px ${fontConfig.family}`;

  const mainMetrics: CustomChartDataset[] = datasets.filter(
    ({ yAxisID, name }) => yAxisID === MAIN_METRIC_ID && name,
  );

  const pages = chunk(mainMetrics, MAX_PER_ROW);
  const [
    currentPage,
    { canGoToPrevStep, canGoToNextStep, goToNextStep, reset },
  ] = useStep(pages.length);
  const maxVisibleLegendCount = currentPage * MAX_PER_ROW;

  const leftOverMetricsCount = mainMetrics.length - maxVisibleLegendCount;
  const nextStepCount =
    leftOverMetricsCount < MAX_PER_ROW ? leftOverMetricsCount : MAX_PER_ROW;

  if (chart && !checkShouldDrawChartLegendInChart(chart)) {
    return <div className="min-h-[8px] mt-1"></div>;
  }

  return (
    <div
      style={{ font }}
      className="flex items-end justify-between min-h-[23px] mt-1 -mb-[15px] z-[1] relative"
    >
      <div className="pt-0.5">{mainMetricName}</div>
      <div className="flex items-center space-x-2">
        <div
          className="grid gap-3 py-1.5"
          style={{
            gridTemplateColumns: `repeat(${
              mainMetrics.length > MAX_PER_ROW
                ? MAX_PER_ROW
                : mainMetrics.length
            }, 1fr)`,
          }}
        >
          {mainMetrics.map(({ name, baseBorderColor }, index) => {
            if (index >= maxVisibleLegendCount) {
              return null;
            }

            return (
              <GenericTooltip
                key={`${name}-${index}`}
                noInfoCircle
                content={name}
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeIn" }}
                  className="flex items-center space-x-1 font-light"
                >
                  <div
                    className="h-3 w-3"
                    style={{ backgroundColor: baseBorderColor }}
                  />
                  <div className="truncate max-w-[120px]">{name}</div>
                </motion.div>
              </GenericTooltip>
            );
          })}
        </div>
        <div className="flex flex-col justify-between h-full">
          {canGoToPrevStep ? (
            <TertiaryButton size="sm" onClick={reset}>
              COLLAPSE
            </TertiaryButton>
          ) : (
            <div />
          )}
          {canGoToNextStep && (
            <TertiaryButton size="sm" onClick={goToNextStep}>
              NEXT {nextStepCount}
            </TertiaryButton>
          )}
        </div>
      </div>
      <div className="pt-0.5">{compare1Name}</div>
    </div>
  );
};
